import React, { useEffect, useMemo, useState } from 'react';
import { Beef, Wheat, CircleDot, Flame, Droplet, Leaf } from 'lucide-react';
import { mobileData as data } from '../data';
import { PlayFab } from '../../app/services/PlayFab';

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="mb-card mb-stat-card">
      <div className="mb-stat-icon solo"><Icon size={12} /></div>
      <div className="mb-stat-label small center">{label}</div>
      <div className="mb-stat-value-row compact">{value}</div>
    </div>
  );
}

function CameraIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="mb-icon-left">
      <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm8 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" />
    </svg>
  );
}

function parseMealMarkdown(markdown: string): { items: Array<{ name: string; quantity: string; calories: number; protein: number; carbs: number; fat: number; fiber: number }>, totals: { calories: number; protein: number; carbs: number; fat: number; fiber: number } } {
  const result = { items: [] as any[], totals: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 } };
  if (!markdown) return result;
  const lines = markdown.split(/\r?\n/);
  let i = 0;
  // find first table
  while (i < lines.length) {
    const t = lines[i].trim();
    if (t.startsWith('|') && (i + 1 < lines.length) && lines[i + 1].includes('---')) {
      const header = t.split('|').map(s => s.trim()).filter(Boolean);
      i += 2; // skip header + separator
      const col = (name: string) => header.findIndex(h => h.toLowerCase().startsWith(name));
      const ci = {
        item: col('item'), qty: col('quantity'), cal: col('calories'), p: col('protein'), c: col('carbs'), fatc: col('fat'), f: col('fiber')
      } as any;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const row = lines[i].trim().split('|').map(s => s.trim()).filter(Boolean);
        const name = row[ci.item] || '';
        const quantity = row[ci.qty] || '';
        const toNum = (s?: string) => {
          if (!s) return 0;
          const m = s.replace(/[^0-9.\-]/g, '');
          const v = parseFloat(m);
          return isNaN(v) ? 0 : v;
        };
        const calories = toNum(row[ci.cal]);
        const protein = toNum(row[ci.p]);
        const carbs = toNum(row[ci.c]);
        const fat = toNum(row[ci.fatc]);
        const fiber = toNum(row[ci.f]);
        if (name) {
          result.items.push({ name, quantity, calories, protein, carbs, fat, fiber });
          result.totals.calories += calories;
          result.totals.protein += protein;
          result.totals.carbs += carbs;
          result.totals.fat += fat;
          result.totals.fiber += fiber;
        }
        i++;
      }
      break;
    }
    i++;
  }
  return result;
}

function formatDate(d: Date) {
  return d.toISOString().substring(0, 10);
}

export default function MacrosPage() {
  const m = data.macro_plan;
  const [date, setDate] = useState<string>(formatDate(new Date()));
  const [meals, setMeals] = useState<Array<{ id: string; timestamp: string; markdown: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const key = `Meals_${date}`;
        const res = await PlayFab.getUserReadOnlyData([key]);
        const raw = res.success && res.data && res.data[key] && res.data[key].Value ? res.data[key].Value : '[]';
        const list = JSON.parse(raw || '[]');
        setMeals(Array.isArray(list) ? list : []);
      } catch {
        setMeals([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [date]);

  const parsed = useMemo(() => {
    const normTotals = (t: any) => ({
      calories: Number((t && (t.calories))) || 0,
      protein: Number((t && (t.protein ?? t.protein_g))) || 0,
      carbs: Number((t && (t.carbs ?? t.carbs_g))) || 0,
      fat: Number((t && (t.fat ?? t.fat_g))) || 0,
      fiber: Number((t && (t.fiber ?? t.fiber_g))) || 0,
    });
    const normItems = (items: any[]) => Array.isArray(items) ? items.map((it) => ({
      name: String(it.name || it.item || ''),
      quantity: String(it.quantity || ''),
      calories: Number(it.calories || 0),
      protein: Number((it.protein ?? it.protein_g) || 0),
      carbs: Number((it.carbs ?? it.carbs_g) || 0),
      fat: Number((it.fat ?? it.fat_g) || 0),
      fiber: Number((it.fiber ?? it.fiber_g) || 0)
    })) : [];
    return meals.map((e: any) => {
      const hasStructured = e && e.totals && typeof e.totals === 'object';
      const parsed = hasStructured
        ? { items: normItems(e.items), totals: normTotals(e.totals) }
        : parseMealMarkdown(e.markdown || '');
      return { entry: e, parsed };
    });
  }, [meals]);

  const dayTotals = useMemo(() => {
    return parsed.reduce((acc, p) => {
      acc.calories += Number(p.parsed.totals.calories) || 0;
      acc.protein += Number(p.parsed.totals.protein) || 0;
      acc.carbs += Number(p.parsed.totals.carbs) || 0;
      acc.fat += Number(p.parsed.totals.fat) || 0;
      acc.fiber += Number(p.parsed.totals.fiber) || 0;
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
  }, [parsed]);

  async function handleDeleteMeal(mealId: string) {
    if (!mealId) return;
    setDeleting(mealId);
    try {
      const res = await PlayFab.executeCloudScript('deleteMealLog', { mealId, date });
      const ok = res.success && res.data?.FunctionResult?.success !== false;
      if (ok) {
        // Optimistically update local state
        setMeals((prev: any[]) => prev.filter((m: any) => (m && m.id) !== mealId));
      }
    } finally {
      setDeleting(null);
    }
  }
  return (
    <div className="mb-stack">
      <div className="mb-row-between">
        <div className="mb-h1">Meals</div>
        <div className="mb-row-between" style={{ gap: 8 }}>
          <button className="mb-btn-secondary small" onClick={() => setDate(formatDate(new Date(new Date(date).getTime() - 86400000)))}>Prev</button>
          <div className="mb-pill">{date}</div>
          <button className="mb-btn-secondary small" onClick={() => setDate(formatDate(new Date(new Date(date).getTime() + 86400000)))}>Next</button>
        </div>
      </div>

      <div className="mb-card">
        <div className="mb-card-title">Daily totals</div>
        <div className="mb-daily-grid">
          <StatCard icon={Beef} label="Protein" value={`${(Number(dayTotals.protein) || 0).toFixed(1)} g`} />
          <StatCard icon={Wheat} label="Carbs" value={`${(Number(dayTotals.carbs) || 0).toFixed(1)} g`} />
          <StatCard icon={Droplet} label="Fat" value={`${(Number(dayTotals.fat) || 0).toFixed(1)} g`} />
          <StatCard icon={Flame} label="Calories" value={`${Math.round(Number(dayTotals.calories) || 0)}`} />
          <StatCard icon={Leaf} label="Fiber" value={`${(Number(dayTotals.fiber) || 0).toFixed(1)} g`} />
        </div>
      </div>

      {loading && <div className="mb-card">Loading meals…</div>}
      {!loading && parsed.length === 0 && <div className="mb-card">No meals saved for this day.</div>}

      {parsed.map(({ entry, parsed }, idx) => (
        <div key={entry.id || idx} className="mb-card">
          <div className="mb-card-title-row" style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:8 }}>
            <div className="mb-card-title">Meal {idx + 1}</div>
            <div className="mb-row-between" style={{ gap: 8, alignItems:'center' }}>
              <div className="mb-muted" style={{ fontSize:12 }}>{new Date(entry.timestamp).toLocaleTimeString()}</div>
              <button
                className="mb-icon-btn"
                title="Delete meal"
                onClick={() => handleDeleteMeal(entry.id)}
                disabled={deleting === entry.id}
              >
                <span role="img" aria-label="delete">🗑️</span>
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="mb-md-table mb-meal-table" style={{ width: '100%' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="left">Item</th>
                  <th className="left">Quantity</th>
                  <th>Calories</th>
                  <th>Protein (g)</th>
                  <th>Carbs (g)</th>
                  <th>Fat (g)</th>
                  <th>Fiber (g)</th>
                </tr>
              </thead>
              <tbody>
                {parsed.items.map((it, rIdx) => (
                  <tr key={rIdx}>
                    <td className="left">{it.name}</td>
                    <td className="left">{it.quantity}</td>
                    <td>{Math.round(Number(it.calories) || 0)}</td>
                    <td>{Math.round(Number((it as any).protein ?? (it as any).protein_g) || 0)}</td>
                    <td>{Math.round(Number((it as any).carbs ?? (it as any).carbs_g) || 0)}</td>
                    <td>{Math.round(Number((it as any).fat ?? (it as any).fat_g) || 0)}</td>
                    <td>{Math.round(Number((it as any).fiber ?? (it as any).fiber_g) || 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="left" colSpan={2}>Total</td>
                  <td>{Math.round(Number(parsed.totals.calories) || 0)}</td>
                  <td>{Math.round(Number(parsed.totals.protein) || 0)}</td>
                  <td>{Math.round(Number(parsed.totals.carbs) || 0)}</td>
                  <td>{Math.round(Number(parsed.totals.fat) || 0)}</td>
                  <td>{Math.round(Number(parsed.totals.fiber) || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}
      <div className="mb-row-between">
        <div>
          <div className="mb-h1">Nutrition plan</div>
          <div className="mb-muted">Goal: {m.goal}</div>
        </div>
        <span className="mb-badge">v{m.calc_config.algorithm_version}</span>
      </div>
      <div className="mb-grid3">
        <StatCard icon={Beef} label="Protein" value={`${m.protein_g} g`} />
        <StatCard icon={Wheat} label="Carbs" value={`${m.carbs_g} g`} />
        <StatCard icon={Droplet} label="Fat" value={`${m.fat_g} g`} />
      </div>
      <div className="mb-card">
        <div className="mb-card-title">Tracking options</div>
        <ul className="mb-list">
          {m.tracking_options.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      <div className="mb-card">
        <div className="mb-card-title">Notes</div>
        <div className="mb-muted">{m.notes}</div>
      </div>
      <div className="mb-grid2">
        <button className="mb-btn">
          <span className="mb-icon-left">🍽️</span> Log a meal
        </button>
        <button className="mb-btn-primary">
          <CameraIcon /> Snap food
        </button>
      </div>
    </div>
  );
}


